/* eslint @typescript-eslint/return-await: 0 */
import {
  type DataProvider as RaDataProvider,
  useDataProvider
} from 'react-admin'
import simpleRestProvider from 'ra-data-simple-rest'
import log, { type LogLevelDesc } from 'loglevel'
import { useQueries, useQuery } from '@tanstack/react-query'

import {
  fetchJson,
  getAnnounces,
  getComment,
  getConfig,
  getDemandAlartSettings,
  getElectricity,
  getElectricityBySpin,
  getElectricityDemand,
  getElectricityDemandMonthly,
  getElectricityDemandDaily,
  getElectricityTargets,
  getHolidays,
  url,
  getElectricityDemandPast
} from './functions'
import {
  ElectricityChartYearlyKind,
  type ElectricityYearly,
  type ElectricityYearlyData,
  type ElectricityDaily,
  type ElectricityDailyData,
  type ElectricityMonthly,
  type ElectricityMonthlyData,
  type ElectricityWeekly,
  type ElectricityWeeklyData,
  type ElectricityChartDailyKind,
  type ElectricityChartMonthlyKind,
  type ElectricityAllYearlyData,
  type AnnounceItem,
  type ElectricityDemandData,
  type ElectricityDailyTargetsData,
  type ElectricityYearlyTargetsData,
  type ElectricityMonthlyTargetsData,
  type ElectricityDemandMonthlyData,
  type Mapping,
  type Comment,
  type ElectricityDemandDailyData
} from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'
import {
  compact,
  filter,
  find,
  flatten,
  flow,
  groupBy,
  includes,
  isNil,
  map,
  reduce,
  reverse,
  size,
  sortBy,
  split
} from 'lodash/fp'
import { format, parseISO, startOfMonth, subYears } from 'date-fns/fp'

log.setLevel((process.env['LOG_LEVEL'] as LogLevelDesc) ?? log.levels.DEBUG)

export const DataProvider = (partner: string | undefined): RaDataProvider => ({
  ...simpleRestProvider(url as string, fetchJson(partner)),
  getElectricityDaily: async (
    date: string,
    compareTo: ElectricityChartDailyKind
  ) => {
    return await getElectricity<ElectricityDaily>(
      partner,
      'daily',
      date,
      compareTo
    )
  },
  getElectricityDailyBySpin: async (
    date: string,
    param: {
      spin: string
      compareTo: ElectricityChartDailyKind
      demand: boolean
    }
  ) => {
    const { spin, compareTo, demand } = param
    return await getElectricityBySpin<ElectricityDaily>(
      partner,
      spin,
      'daily',
      date,
      compareTo,
      demand
    )
  },
  getElectricityDailyTargets: async (
    date: string,
    param: {
      targetKeys: string[]
      targetValues: string[]
    }
  ) => {
    const { targetKeys, targetValues } = param
    return await getElectricityTargets<ElectricityDailyTargetsData>(
      partner,
      'daily2',
      date,
      targetKeys,
      targetValues
    )
  },
  getElectricityWeekly: async (date: string) =>
    await getElectricity<ElectricityWeekly>(partner, 'weekly', date),
  getElectricityWeeklyBySpin: async (date: string, param: { spin: string }) => {
    const { spin } = param
    return await getElectricityBySpin<ElectricityWeekly>(
      partner,
      'weekly',
      date,
      spin
    )
  },
  getElectricityMonthly: async (
    date: string,
    compareTo: ElectricityChartMonthlyKind
  ): Promise<ElectricityMonthly | undefined> =>
    await getElectricity<ElectricityMonthly>(partner, 'month', date, compareTo),
  getElectricityMonthlyBySpin: async (
    date: string,
    param: {
      spin: string
      compareTo: ElectricityChartMonthlyKind
      demand: boolean
    }
  ): Promise<ElectricityMonthly | undefined> => {
    const { spin, compareTo, demand } = param
    return await getElectricityBySpin<ElectricityMonthly>(
      partner,
      spin,
      'month',
      date,
      compareTo,
      demand
    )
  },
  getElectricityMonthlyTargets: async (
    date: string,
    param: {
      targetKeys: string[]
      targetValues: string[]
    }
  ) => {
    const { targetKeys, targetValues } = param
    return await getElectricityTargets<ElectricityMonthlyTargetsData>(
      partner,
      'month2',
      date,
      targetKeys,
      targetValues
    )
  },
  getElectricityMonthlyAverageBySpin: async (
    date: string,
    param: { spin: string }
  ): Promise<ElectricityYearly | undefined> => {
    const { spin } = param
    return await getElectricityBySpin<ElectricityYearly>(
      partner,
      spin,
      'yearAverage',
      date
    )
  },
  getElectricityYearly: async (
    date: string,
    compareTo: ElectricityChartYearlyKind
  ): Promise<ElectricityAllYearlyData | undefined> =>
    await getElectricity<ElectricityAllYearlyData>(
      partner,
      'year/data',
      date,
      compareTo
    ),
  getElectricityYearlyBySpin: async (
    date: string,
    param: {
      spin: string
      compareTo: ElectricityChartYearlyKind
      demand: boolean
    }
  ): Promise<ElectricityYearly | undefined> => {
    const { spin, compareTo, demand } = param
    return await getElectricityBySpin<ElectricityYearly>(
      partner,
      spin,
      'year',
      date,
      compareTo,
      demand
    )
  },
  getElectricityYearlyTargets: async (
    date: string,
    param: {
      targetKeys: string[]
      targetValues: string[]
    }
  ) => {
    const { targetKeys, targetValues } = param
    return await getElectricityTargets<ElectricityYearlyTargetsData>(
      partner,
      'year',
      date,
      targetKeys,
      targetValues
    )
  },
  getElectricityManyYearlyBySpin: async (
    date: string,
    param: { spins: string[] }
  ): Promise<Array<{ data: ElectricityYearly | undefined }>> => {
    const { spins } = param
    if (size(spins) < 5) {
      const data = compact(
        await Promise.all(
          map(
            async (spin: string) =>
              await getElectricityBySpin<{ data: ElectricityYearly }>(
                partner,
                spin,
                'year',
                date
              )
          )(spins)
        )
      )
      return data
    } else {
      const all = await getElectricity<ElectricityAllYearlyData>(
        partner,
        'year',
        date
      )
      if (isNil(all)) {
        return []
      }
      return map((data: ElectricityYearly) => ({ data }))(
        filter((item: ElectricityYearly) => includes(item.spin)(spins))(
          all.data
        )
      )
    }
  },
  getHolidays: async (
    year: number
  ): Promise<Record<string, string> | undefined> => await getHolidays(year),
  getConfig: async (
    partner: string
  ): Promise<Record<string, any> | undefined> => await getConfig(partner),
  getAnnounces: async (partner: string): Promise<AnnounceItem[] | undefined> =>
    await getAnnounces(partner),
  getElectricityDemand: async (
    date: string,
    param: {
      spin: string
    }
  ) => {
    const { spin } = param
    return await getElectricityDemand(partner, spin, date)
  },
  getElectricityDemandPast: async (
    date: string,
    param: {
      spin: string
    }
  ) => {
    const { spin } = param
    return await getElectricityDemandPast(partner, spin, date)
  },
  getElectricityDemandMonthly: async (
    date: string,
    param: {
      spin: string | null | undefined
    }
  ) => {
    const { spin } = param
    return await getElectricityDemandMonthly(partner, spin, date)
  },
  getElectricityDemandDaily: async (
    date: string,
    param: {
      spin: string | null | undefined
    }
  ) => {
    const { spin } = param
    return await getElectricityDemandDaily(partner, spin, date)
  },
  getDemandAlartSettings: async (partner: string) => {
    return await getDemandAlartSettings(partner)
  }
})
export const useGetElectricityDaily = (
  date: string,
  compareTo: ElectricityChartDailyKind
): { isLoading: boolean; data: ElectricityDailyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDaily', date, compareTo],
    queryFn: () => dataProvider['getElectricityDaily'](date, compareTo)
  })
  return { isLoading, data }
}
export const useGetElectricityDailyBySpin = (
  date: string,
  spin: string,
  demand: boolean
): { isLoading: boolean; data: ElectricityDailyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDaily', date, spin, demand],
    queryFn: () =>
      dataProvider['getElectricityDailyBySpin'](date, {
        spin,
        demand
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDailyCompareToBySpin = (
  date: string,
  spin: string,
  compareTo: ElectricityChartDailyKind[] | undefined
): {
  isLoading: boolean
  data:
    | Array<{
        data: ElectricityDailyData | undefined
        compareTo: ElectricityChartDailyKind
      }>
    | undefined
} => {
  const dataProvider = useDataProvider()
  const result = useQueries({
    queries: map((item: ElectricityChartDailyKind) => ({
      queryKey: ['electricityDaily', date, spin, item],
      queryFn: () =>
        dataProvider['getElectricityDailyBySpin'](date, {
          spin,
          compareTo: item
        }),
      staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
    }))(compareTo),
    combine: (results) => ({
      isLoading: !isNil(
        find(
          (item: any) => !isNil(item.isLoading) && (item.isLoading as boolean)
        )(results)
      ),
      // @ts-expect-error ts(2339)
      data: map.convert({ cap: false })((item: any, index: number) => ({
        data: item.data,
        compareTo: isNil(compareTo) ? undefined : compareTo[index]
      }))(results)
    })
  })

  return result
}
export const useGetElectricityDailies = (
  date: string,
  spins: string[]
): {
  isLoading: boolean
  data: Array<ElectricityDailyData | undefined> | undefined
} => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDaily', date, spins],
    queryFn: async () =>
      await Promise.all(
        map(
          async (spin: string) =>
            await dataProvider['getElectricityDailyBySpin'](date, {
              spin,
              demand: false
            })
        )(spins)
      ),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityWeekly = (
  date: string
): { isLoading: boolean; data: ElectricityWeeklyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityWeekly', date],
    queryFn: () => dataProvider['getElectricityWeekly'](date),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityWeeklyBySpin = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityWeeklyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityWeekly', date, spin],
    queryFn: () => dataProvider['getElectricityWeeklyBySpin'](date, { spin }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthly = (
  date: string,
  compareTo: ElectricityChartMonthlyKind
): { isLoading: boolean; data: ElectricityMonthlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthly', date, compareTo],
    queryFn: () => dataProvider['getElectricityMonthly'](date, compareTo),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyBySpin = (
  date: string,
  spin: string,
  demand: boolean = false
): { isLoading: boolean; data: ElectricityMonthlyData | null | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthly', date, spin, demand],
    queryFn: () =>
      dataProvider['getElectricityMonthlyBySpin'](date, {
        spin,
        demand
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyCompareToBySpin = (
  date: string,
  spin: string,
  compareTo: ElectricityChartMonthlyKind[] | undefined
): {
  isLoading: boolean
  data:
    | Array<{
        data: ElectricityMonthlyData | undefined
        compareTo: ElectricityChartMonthlyKind
      }>
    | undefined
} => {
  const dataProvider = useDataProvider()
  const result = useQueries({
    queries: map((item: ElectricityChartMonthlyKind) => ({
      queryKey: ['electricityMonthly', date, spin, item],
      queryFn: () =>
        dataProvider['getElectricityMonthlyBySpin'](date, {
          spin,
          compareTo: item
        }),
      staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
    }))(compareTo),
    combine: (results) => ({
      isLoading: !isNil(
        find(
          (item: any) => !isNil(item.isLoading) && (item.isLoading as boolean)
        )(results)
      ),
      // @ts-expect-error ts(2339)
      data: map.convert({ cap: false })((item: any, index: number) => ({
        data: item.data,
        compareTo: isNil(compareTo) ? undefined : compareTo[index]
      }))(results)
    })
  })

  return result
}
export const useGetElectricityMonthlies = (
  date: string,
  spins: string[]
): {
  isLoading: boolean
  data: Array<ElectricityMonthlyData | undefined> | undefined
} => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthly', date, spins],
    queryFn: async () =>
      await Promise.all(
        map(
          async (spin: string) =>
            await dataProvider['getElectricityMonthlyBySpin'](date, {
              spin,
              demand: false
            })
        )(spins)
      ),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyAverageBySpin = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityYearlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthlyAverage', date, spin],
    queryFn: () =>
      dataProvider['getElectricityMonthlyAverageBySpin'](date, { spin })
    // staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearly = (
  date: string,
  compareTo: ElectricityChartYearlyKind
): { isLoading: boolean; data: ElectricityAllYearlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityYearly', date, compareTo],
    queryFn: () => dataProvider['getElectricityYearly'](date, compareTo),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearlyBySpin = (
  date: string,
  spin: string,
  demand: boolean = false
): { isLoading: boolean; data: ElectricityYearlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityYearly', date, spin, demand],
    queryFn: () =>
      dataProvider['getElectricityYearlyBySpin'](date, {
        spin,
        demand
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearlyCompareToBySpin = (
  date: string,
  spin: string,
  compareTo: ElectricityChartYearlyKind[] | undefined
): {
  isLoading: boolean
  data:
    | Array<{
        data: ElectricityYearlyData | undefined
        compareTo: ElectricityChartYearlyKind
      }>
    | undefined
} => {
  const dataProvider = useDataProvider()
  const result = useQueries({
    queries: map((item: ElectricityChartYearlyKind) => ({
      queryKey: ['electricityYearly', date, spin, item],
      queryFn: () =>
        dataProvider['getElectricityYearlyBySpin'](date, {
          spin,
          compareTo: item
        }),
      staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
    }))(compareTo),
    combine: (results) => ({
      isLoading: !isNil(
        find(
          (item: any) => !isNil(item.isLoading) && (item.isLoading as boolean)
        )(results)
      ),
      // @ts-expect-error ts(2339)
      data: map.convert({ cap: false })((item: any, index: number) => ({
        data: item.data,
        compareTo: isNil(compareTo) ? undefined : compareTo[index]
      }))(results)
    })
  })

  return result
}

export const useGetElectricityManyYearlyBySpin = (
  date: string,
  spins: string[]
): { isLoading: boolean; data: ElectricityYearlyData[] } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityYearly',
      date,
      size(spins) < 5 ? JSON.stringify(spins) : 'all'
    ],
    queryFn: () =>
      dataProvider['getElectricityManyYearlyBySpin'](date, { spins }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetHolidays = (
  year: number
): { isLoading: boolean; data: Record<string, string> | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => dataProvider['getHolidays'](year),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetConfig = (
  partner: string
): { isLoading: boolean; data: Record<string, any> | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['config', partner],
    queryFn: () => dataProvider['getConfig'](partner),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetAnnounces = (
  partner: string
): { isLoading: boolean; data: AnnounceItem[] } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['announces', partner],
    queryFn: () => dataProvider['getAnnounces'](partner),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return {
    isLoading,
    data: flow([sortBy<AnnounceItem>('date'), reverse])(data)
  }
}
export const useGetElectricityDemand = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityDemandData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDemand', date, spin],
    queryFn: () =>
      dataProvider['getElectricityDemand'](date, {
        spin
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDemandPast = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityDemandData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDemandPast', date, spin],
    queryFn: () =>
      dataProvider['getElectricityDemandPast'](date, {
        spin
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDemandMonthly = (
  dateArg: string,
  spin?: string
): { isLoading: boolean; data: ElectricityDemandMonthlyData | undefined } => {
  const date = format('yyyy-MM-dd')(startOfMonth(parseISO(dateArg)))
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDemandMonthly', date, spin],
    queryFn: () =>
      dataProvider['getElectricityDemandMonthly'](date, {
        spin
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDemandDaily = (
  dateArg: string,
  spin?: string
): { isLoading: boolean; data: ElectricityDemandDailyData | undefined } => {
  const date = format('yyyy-MM-dd')(parseISO(dateArg))
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDemandDaily', date, spin],
    queryFn: () =>
      dataProvider['getElectricityDemandDaily'](date, {
        spin
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDailyTargets = (
  date: string,
  targetKeys?: string[],
  targetValues?: string[]
): { isLoading: boolean; data: ElectricityDailyTargetsData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityDaily',
      date,
      JSON.stringify(targetKeys ?? []),
      JSON.stringify(targetValues ?? [])
    ],
    queryFn: () =>
      dataProvider['getElectricityDailyTargets'](date, {
        targetKeys,
        targetValues
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyTargets = (
  date: string,
  targetKeys?: string[],
  targetValues?: string[]
): { isLoading: boolean; data: ElectricityMonthlyTargetsData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityMonthly',
      date,
      JSON.stringify(targetKeys ?? []),
      JSON.stringify(targetValues ?? [])
    ],
    queryFn: () =>
      dataProvider['getElectricityMonthlyTargets'](date, {
        targetKeys,
        targetValues
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearlyTargets = (
  date: string,
  compareTo: ElectricityChartYearlyKind,
  targetKeys?: string[],
  targetValues?: string[]
): { isLoading: boolean; data: ElectricityYearlyTargetsData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityYearly',
      date,
      compareTo,
      JSON.stringify(targetKeys ?? []),
      JSON.stringify(targetValues ?? [])
    ],
    queryFn: () =>
      dataProvider['getElectricityYearlyTargets'](date, {
        compareTo,
        targetKeys,
        targetValues
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetMappingsTotal = (
  partner: string,
  all: boolean
): { total: number | undefined; isLoading: boolean } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['mappings', partner, all],
    queryFn: async () => {
      const { total } = await dataProvider.getList(
        all ? 'mappingAlls' : 'mappings',
        {
          pagination: { perPage: 1, page: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: {}
        }
      )
      return { total, isLoading: isNil(total) }
    },
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  const { total } = data ?? { total: undefined }
  return { isLoading, total }
}
export const useGetMappings = (
  partner: string,
  all: boolean = false
): { data: Mapping[] | undefined; isLoading: boolean } => {
  const dataProvider = useDataProvider()
  const { total } = useGetMappingsTotal(partner, all)
  const { data, isLoading } = useQuery({
    queryKey: ['mappings', partner, total, all],
    queryFn: async () =>
      isNil(total)
        ? { data: undefined }
        : dataProvider.getList(all ? 'mappingAlls' : 'mappings', {
            pagination: { perPage: total, page: 1 },
            sort: { field: 'id', order: 'ASC' },
            filter: {}
          }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data: isNil(data) ? undefined : data.data }
}
export const useGetReducedElectricityYearly = (
  partner: string,
  date: string,
  targetDate: string
): { data: any; isLoading: boolean; isFetching: boolean } => {
  const { data: mappings, isLoading: isLoadingMappings } =
    useGetMappings(partner)
  const { data: rawData, isLoading: isLoadingData } = useGetElectricityYearly(
    date,
    ElectricityChartYearlyKind.lastYear
  )
  const reduceData = (
    rawData: ElectricityYearly[],
    spins: string[],
    date: string
  ): number => {
    const targets = filter((item: ElectricityYearly) =>
      includes(item.spin)(spins)
    )(rawData)
    const data = groupBy<{ date: string; daily: number | null }>('date')(
      flatten(map((item: ElectricityYearly) => item.amounts)(targets))
    )
    return Math.floor(
      reduce(
        (sum: number, item: { daily: number | null }) =>
          sum + (item.daily ?? 0),
        0
      )(data[date])
    )
  }
  const info = useQuery({
    queryKey: [
      'ReducedElectricityYearly',
      partner,
      date,
      targetDate,
      isLoadingData,
      isLoadingMappings
    ],
    queryFn: async () => {
      if (isLoadingData || isLoadingMappings) {
        return { data: undefined, isLoading: true }
      }
      if (isNil(rawData) || isNil(mappings)) {
        return { data: undefined, isLoading: true }
      }
      const sum = reduce(
        (
          sum: {
            values: number[]
            lastValues: number[]
            compareValues: number[]
          },
          item: Mapping
        ) => {
          const spins = split(',')(item.spin)
          const value = reduceData(compact(rawData.data), spins, targetDate)
          const lastValue = reduceData(
            compact(rawData.compareData),
            spins,
            format('yyyy-MM-dd')(subYears(1)(parseISO(targetDate)))
          )
          const compareValue = Math.floor(((value ?? 0) / lastValue) * 100)
          sum.values.push(value)
          sum.lastValues.push(lastValue)
          sum.compareValues.push(compareValue)
          return sum
        },
        { values: [], lastValues: [], compareValues: [] }
      )(mappings)
      return { data: { mappings, ...sum }, isLoading: false }
    },
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  const { data, isFetching } = info
  return !isNil(data) ? info : { data: undefined, isLoading: true, isFetching }
}
export const useGetTotal = (
  partner: string | undefined,
  resource: string
): { total: number | undefined; isLoading: boolean } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [resource, partner],
    queryFn: async () => {
      const { total } = await dataProvider.getList(resource, {
        pagination: { perPage: 1, page: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
      return { total, isLoading: isNil(total) }
    },
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  const { total } = data ?? { total: undefined }
  return { isLoading, total }
}
export const useGetAccounts = (
  partner: string | undefined
): { data: Mapping[] | undefined; isLoading: boolean } => {
  const dataProvider = useDataProvider()
  const { total } = useGetTotal(partner, 'accounts')
  const { data, isLoading } = useQuery({
    queryKey: ['mappings', partner, total],
    queryFn: async () =>
      isNil(total)
        ? undefined
        : dataProvider.getList('accounts', {
            pagination: { perPage: total, page: 1 },
            sort: { field: 'id', order: 'ASC' },
            filter: {}
          }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data: isNil(data) ? undefined : data.data }
}
export const useGetComments = (
  partner: string | null | undefined,
  date: string | null | undefined
): { data: Comment[] | undefined; isLoading: boolean } => {
  const { data, isLoading } = useQuery({
    queryKey: ['comment', partner, date],
    queryFn: async () =>
      isNil(partner) || isNil(date) ? [] : getComment(partner, date),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetDemandAlartSettings = (
  partner: string
): { data: { threshold: number; targets: string[] }; isLoading: boolean } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['demandAlartSettings', partner],
    queryFn: () => dataProvider['getDemandAlartSettings'](partner),
    staleTime: 0
  })
  return {
    isLoading,
    data
  }
}
